<script setup lang="ts">
import getYouTubeID from "get-youtube-id";

const playbackStore = useFullSongPlaybackStore();
const store = useMediaPlayerStore();
const youtube = useYoutubeStore();
const beta = useBetaStore();
const debug = useDebug();
const { t } = useI18n();
const modals = useModalStore();
const enableSpotify =
  import.meta.env.VITE_ENABLE_SPOTIFY === "1" ||
  import.meta.env.VITE_ENABLE_SPOTIFY === "new";

defineProps<{ progressBarOnly?: boolean }>();

const playPauseIcon = computed(() => {
  let icon = "fa-solid fa-play";
  if (store.status === MediaPlayerStatus.Loading) {
    icon = "fa-solid fa-spinner-third fa-spin";
  } else if (store.status === MediaPlayerStatus.Paused) {
    icon = "fa-solid fa-play";
  } else if (store.status === MediaPlayerStatus.Ended) {
    icon = "fa-solid fa-play";
  } else if (store.status === MediaPlayerStatus.Unloaded) {
    icon = "fa-solid fa-play";
  } else if (store.status === MediaPlayerStatus.Error) {
    icon = "fa-solid fa-play";
  } else {
    icon = "fa-solid fa-pause";
  }
  return icon;
});

const status = computed(() => {
  switch (store.status) {
    case MediaPlayerStatus.Ended:
      return "Ended";
    case MediaPlayerStatus.Loading:
      return "Loading";
    case MediaPlayerStatus.Error:
      return "Error";
    case MediaPlayerStatus.Paused:
      return "Paused";
    case MediaPlayerStatus.Playing:
      return "Playing";
    case MediaPlayerStatus.Unloaded:
      return "Unloaded";
  }
});
const playPauseIconLabel = computed(() => {
  let icon = "Play";
  if (store.status === MediaPlayerStatus.Loading) {
    icon = "Loading";
  } else if (store.status === MediaPlayerStatus.Paused) {
    icon = "Play";
  } else if (store.status === MediaPlayerStatus.Ended) {
    icon = "Play";
  } else if (store.status === MediaPlayerStatus.Unloaded) {
    icon = "Play";
  } else if (store.status === MediaPlayerStatus.Error) {
    icon = "Play";
  } else {
    icon = "Pause";
  }
  return icon;
});

let beforeDragStatus = MediaPlayerStatus.Loading;

function onDragStart(time: number) {
  beforeDragStatus = store.status;
  store.pause();
  store.goToTime(time);
}

function onDragMove(time: number) {
  store.goToTime(time);
}

async function onDragEnd(time: number) {
  debug.log("on drag end - go to time", time);
  await store.goToTime(time, true);
  if (
    beforeDragStatus === MediaPlayerStatus.Playing ||
    beforeDragStatus === MediaPlayerStatus.Loading
  ) {
    debug.log("play");
    store.play();
  }
}

const player = computed(() => store.mediaPlayer);

const sourceLogo = computed(() => {
  let source = player.value;
  if (!store.nowPlaying) {
    source = store.determineMediaPlayer(store.mediaItems[0]);
  }
  if (!nowPlaying.value) {
    return null;
  }

  if (source === "html-audio") {
    if (
      nowPlaying.value.previewUrl?.includes("apple.com") &&
      nowPlaying.value.urls.find((url) => url.source === "iTunes")
    ) {
      return "apple";
    } else {
      return "";
    }
  }
  return source;
});

const sourceTitle = computed(() => {
  if (!nowPlaying.value) {
    return "";
  } else if (sourceLogo.value === "youtube") {
    return t("SV.LIT_SeeOnYoutube", {
      title: nowPlaying.value.title,
    });
  } else if (sourceLogo.value === "apple") {
    return t("SV.MES_AppleMusicPlaying", {
      title: nowPlaying.value.title,
    });
  } else if (sourceLogo.value === "spotify") {
    return t("SV.MES_SpotifyPlaying", { title: nowPlaying.value.title });
  }
});

function getYoutubeLink() {
  const url = store.nowPlaying?.urls[0]?.url;
  if (!url) {
    return null;
  }
  const videoId = getYouTubeID(url || "");
  const isUrlValid =
    typeof url === "string" ? youtube.isYouTubeURLValid(url) : null;
  if (!videoId && !isUrlValid) {
    throw new CustomError(
      {
        songNumber: store.song?.ccliSongNumber,
        videoUrl: url,
      },
      false,
      `Invalid Youtube Url - ${url}`,
    );
  }
  youtube.isVideoAvailable(url || "");
  return `https://www.youtube.com/watch?v=${videoId}`;
}

const sourceUrl = computed(() => {
  let source = player.value;
  if (!store.nowPlaying) {
    source = store.determineMediaPlayer(store.mediaItems[0]);
  }

  if (!nowPlaying.value) {
    return null;
  }

  if (source === "youtube") {
    return getYoutubeLink();
  } else if (source === "html-audio") {
    if (
      nowPlaying.value.previewUrl?.includes("apple.com") &&
      nowPlaying.value.urls.find((url) => url.source === "iTunes")
    ) {
      return nowPlaying.value.urls.find((url) => url.source === "iTunes")?.url;
    }
  } else if (source === "apple") {
    return nowPlaying.value.urls.find((url) => url.source === "iTunes")?.url;
  } else if (source === "spotify") {
    return nowPlaying.value.urls.find((url) => url.source === "Spotify")?.url;
  }
  return null;
});

const nowPlaying = computed(() => store.nowPlaying || store.mediaItems[0]);

function togglePlay() {
  if (store.nowPlaying) {
    store.togglePlay();
  } else if (store.mediaItems.length) {
    store.playMediaItem(store.mediaItems[0].id);
  }
}
</script>

<template>
  <div class="media-player-expanded">
    <div class="media-player-expanded-inner">
      <NowPlaying
        v-if="!youtube.isFullScreen && !progressBarOnly"
        class="now-playing"
      />

      <div
        class="now-playing-wrapper"
        :class="{
          'progress-bar-only': progressBarOnly,
        }"
      >
        <div class="controls">
          <MediaPlayerButton
            id="SongDetailsPlaybackTogglePlay"
            :icon="playPauseIcon"
            :data-status="status"
            :data-title="nowPlaying?.title"
            :data-id="nowPlaying?.id"
            :aria-label="playPauseIconLabel"
            @click="togglePlay"
          />
        </div>
        <div class="progress-bar-wrapper">
          <ProgressBar
            id="SongDetailsPlaybackProgressBar"
            :progress="store.progress ?? 0"
            :preview-mode="false"
            :length-seconds="store.duration ?? 0"
            :preview-length-seconds="nowPlaying?.previewLengthSeconds ?? 0"
            @dragstart="onDragStart"
            @dragmove="onDragMove"
            @dragend="onDragEnd"
          />
          <div
            v-if="
              beta.features.fullSongPlayback &&
              !playbackStore.integration &&
              nowPlaying?.urls.find((url) => url.source === 'iTunes')
            "
            class="preview-text preview-text-desktop"
          >
            {{ $t("SV.LIT_PreviewProvidedByAppleMusic") }}
            <button
              type="button"
              class="link"
              @click="modals.showModal('PreferenceModal')"
            >
              <span v-if="enableSpotify">{{
                $t("SV.MES_ConnectWithAppleMusicSpotify")
              }}</span>
              <span v-else>{{ $t("SV.MES_ConnectWithAppleMusic") }}</span>
            </button>
          </div>
        </div>

        <template v-if="sourceLogo && sourceUrl">
          <div
            class="settings-wrapper"
            :title="sourceTitle"
          >
            <a
              :href="sourceUrl"
              target="_blank"
              class="logo"
              @click="store.pause()"
            >
              <YoutubeLogo v-if="sourceLogo === 'youtube'" />
              <AppleMusicLogo v-else-if="sourceLogo === 'apple'" />
              <SpotifyLogo v-else-if="sourceLogo === 'spotify'" />
            </a>
          </div>
        </template>
      </div>
    </div>
    <div
      v-if="beta.features.fullSongPlayback && !playbackStore.integration"
      class="preview-text preview-text-mobile"
    >
      {{ $t("SV.LIT_PreviewProvidedByAppleMusic") }}
      <button
        type="button"
        class="link"
        @click="modals.showModal('PreferenceModal')"
      >
        <span v-if="enableSpotify">{{
          $t("SV.MES_ConnectWithAppleMusicSpotify")
        }}</span>
        <span v-else>{{ $t("SV.MES_ConnectWithAppleMusic") }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.media-player-expanded {
  @apply bg-black bg-opacity-15 px-5 py-3;
}
.media-player-expanded-inner {
  @apply flex min-h-24 w-full flex-col items-start justify-between gap-2 rounded-none sm:min-h-0 sm:flex-row sm:items-center sm:gap-5;
}
.now-playing-wrapper {
  @apply ms-0 flex w-full flex-1 items-center gap-5;
  &:not(.progress-bar-only) {
    @apply sm:ms-8 xl:ms-20;
  }
}
.now-playing {
  @apply w-full max-w-[350px];
}
.preview-text {
  @apply text-center text-xs opacity-75;
}

.preview-text-mobile {
  @apply w-full 2xl:hidden;
}

.preview-text-desktop {
  @apply absolute bottom-0 left-0 right-0 max-2xl:hidden;
}

.progress-bar-wrapper {
  @apply relative flex-1;
}

.logo {
  @apply block w-[100px];
}
.settings-wrapper {
  @apply block;
}
</style>
